/* eslint-disable react-hooks/exhaustive-deps */
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Form } from 'antd';
import authApi from 'api/auth';
import env from 'app/env';
import { useAppDispatch, useAppTranslation, useErrTranslation } from 'app/hooks';
import VerticalForm from 'components/Form/VerticalForm';
import FieldError from 'components/FormField/FieldError';
import PasswordField from 'components/FormField/PasswordField';
import { showErrorModal } from 'components/Modals/CommonModals';
import { onChangeAuthPage } from 'features/home/homeSlice';
import { useHandleResponseError } from 'hooks/useHandleResponseError';
import useUserDetail from 'hooks/useUserDetail';
import { LoginFormRequestModel } from 'models/auth';
import React, { useEffect, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { FaLock } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { setLocalStorage } from 'utils/sessionStorage';
import TextField from '../../../components/FormField/TextField';
import AppleAuth from '../sso/AppleAuth';
import GoogleAuth from '../sso/GoogleAuth';
import TwitterAuth from '../sso/TwitterAuth';

interface LoginPageProps {
  isResetFields?: boolean;
  onShowForgotPassword: () => void;
  onShowResendActiveLink: () => void;
  onChangeLoadingStatus: (value: boolean) => void;
}

const LoginPage: React.FunctionComponent<LoginPageProps> = ({
  isResetFields = false,
  onShowForgotPassword,
  onShowResendActiveLink,
  onChangeLoadingStatus,
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const t = useAppTranslation();
  const et = useErrTranslation();
  const handleResponseError = useHandleResponseError();
  const { getUserDetail } = useUserDetail();
  const [haveError, setHaveError] = useState<boolean>(false);
  const [isValidateCapcha, setIsValidateCapcha] = useState<boolean>(false);

  const onSubmitLoginForm = async (value: LoginFormRequestModel) => {
    onChangeLoadingStatus(true);

    const { ok, error, body } = await authApi.login({
      ...value,
    });

    onChangeLoadingStatus(false);

    if (ok && body) {
      setLocalStorage('access_token_terabet', body.accessToken);
      dispatch(onChangeAuthPage(-1));
    }
    if (error) {
      if (error.message === 'error.validate.user.inactive') {
        const onOk = async () => {
          onShowResendActiveLink();
        };
        showErrorModal({
          content: et(error.message),
          okText: t('resend'),
          onOk: onOk,
        });
      } else {
        handleResponseError(error);
      }
    }
    getUserDetail();
  };

  const handleFieldsChange = () => {
    setHaveError(form.getFieldsError().some((error) => error.errors.length));
  };

  useEffect(() => {
    form.resetFields();
  }, [isResetFields]);

  return (
    <VerticalForm
      className="auth__login relative"
      name="login"
      form={form}
      onFinish={onSubmitLoginForm}
      onFieldsChange={handleFieldsChange}
    >
      <TextField
        label={t('login.form.email')}
        name="email"
        placeholder={t('login.placeholder.email')}
        prefix={<MdEmail />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.email.required')} />,
          },
          {
            type: 'email',
            message: <FieldError message={t('errors.email.invalid')} />,
          },
        ]}
      />
      <PasswordField
        label={t('login.form.password')}
        name="password"
        placeholder={t('login.placeholder.password')}
        prefix={<FaLock />}
        className="input_primary"
        rules={[
          {
            required: true,
            message: <FieldError message={t('errors.password.required')} />,
          },
        ]}
      />
      <div className="login__footer">
        <button type="button" className="btn-reset-password" onClick={onShowForgotPassword}>
          {t('login.form.forgotPassword')}
        </button>
      </div>
      <div className="capcha">
        <ReCAPTCHA
          sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA || ''}
          onChange={(token) => setIsValidateCapcha(Boolean(token))}
        />
      </div>

      <button
        disabled={haveError || !isValidateCapcha}
        type="submit"
        className={`btn_submit ${haveError || !isValidateCapcha ? 'disabled' : ''}`}
      >
        {t('login.title')}
      </button>
      <div className="auth-form__footer">
        <div className="divider-text">
          <span>{t('login.or')}</span>
        </div>
      </div>
      <div className="social-logins">
        <GoogleOAuthProvider clientId={env.googleClientId}>
          <GoogleAuth />
        </GoogleOAuthProvider>
        <TwitterAuth />
        <AppleAuth />
      </div>
    </VerticalForm>
  );
};

export default LoginPage;
